import { IRules } from "../interfaces/IRules";
import { ActionDelegate, DefinedAction } from "./RpgActions";
import { IRulesDefinition } from "../interfaces/IRulesDefinition";
import { RuleModule } from "./RuleModule";
import { RuleItem } from "../interfaces/RuleItem";
import { IRuleModule } from "../interfaces/IRuleModule";
import { AddAlert } from "./AlertCenter";
import { ActionResult } from "../interfaces/ActionResult";
import { AlertLevel } from "../types/AlertTypes";

export class Rules implements IRules {
  ruleModules: RuleModule[][] = [];
  ruleModuleIndex: { [key: string]: number } = {};
  ruleItemKeys: { [key: string]: string } = {};
  moduleItemNameToModuleKey: { [key: string]: string } = {};
  addActionsOrReset: any;

  public externalCallback = () => {
    alert("Rules external callback not implemented for this instance!");
  };

  public addModuleAlert(key: string, actionResult: ActionResult): ActionResult {
    const message = "Rules alerting not implemented for this instance!";
    alert(message);
    return { message, success: false, level: AlertLevel._0_CRITICAL };
  }

  public addItemAlert(key: string, actionResult: ActionResult): ActionResult {
    const message = "Rules alerting not implemented for this instance!";
    alert(message);
    return { message, success: false, level: AlertLevel._0_CRITICAL };
  }

  constructor(
    externalCallback: () => void,
    defaultRules: IRulesDefinition,
    addActionsOrReset: any,
    addModuleAlert: AddAlert,
    addItemAlert: AddAlert
  ) {
    this.externalCallback = externalCallback;
    this.addModuleAlert = addModuleAlert;
    this.addItemAlert = addItemAlert;
    defaultRules.ruleModules.forEach(
      (section: IRuleModule[], section_i: number) => {
        this.ruleModules.push([]);
        section.forEach((ruleModule: IRuleModule) => {
          this.addRuleModule(section_i, ruleModule);
        });
      }
    );

    this.updateRuleModuleIndex();
    this.addActionsOrReset = addActionsOrReset;
    this.setActionDelegates();
  }

  public refreshRules = () => {
    this.updateRuleModuleIndex();
    this.setActionDelegates();
  };

  public setActionDelegates = (): void => {
    // TODO: call again if rule modules get added or removed
    const actions: DefinedAction[] = [];

    // TODO: more dynamic parameters based on module type
    for (let ruleItemName in this.moduleItemNameToModuleKey) {
      actions.push({
        actionName: `add${ruleItemName}`,
        paramNames: ["Name", "Key", "Bonus Source Keys"],
        allowOneLess: true,
        actionCallback: (
          name: string,
          key: string,
          ...bonusSources: string[]
        ) => {
          return this.addRuleItem(
            name,
            key,
            this.moduleItemNameToModuleKey[ruleItemName],
            ...bonusSources
          );
        },
      });
      actions.push({
        actionName: `delete${ruleItemName}`,
        paramNames: ["Key"],
        allowOneLess: false,
        actionCallback: (key: string) => {
          return this.deleteRuleItem(
            key,
            this.moduleItemNameToModuleKey[ruleItemName]
          );
        },
      });
    }
    const actionDelegate: ActionDelegate = {
      delegateKey: "rules",
      newActions: actions,
    };
    this.addActionsOrReset(actionDelegate);
  };

  public addKeyCallback = (
    key: string,
    triggerUI: boolean,
    moduleKey?: string
  ): ActionResult => {
    const result: ActionResult = this.checkKeyDuplicateCallback(key, triggerUI);
    if (!result.success) {
      return result;
    }
    if (moduleKey) {
      this.ruleItemKeys[key] = moduleKey;
    }
    if (triggerUI) {
      this.externalCallback();
    }
    return {
      success: true,
      message: `Key '${key}' was added.`,
      level: AlertLevel._4_LOG,
    };
  };

  public checkKeyDuplicateCallback = (
    key: string,
    triggerUI: boolean
  ): ActionResult => {
    if (key in this.ruleItemKeys) {
      if (!triggerUI) {
        alert(`Duplicate item key '${key}'.`);
      }
      return this.addItemAlert(key, {
        success: false,
        message: `Key ${key} is a duplicate.`,
        level: AlertLevel._2_RULE,
        triggerUI,
      });
    }
    return {
      success: true,
      message: `Key ${key} doesn't appear to be a duplicate.`,
      level: AlertLevel._5_VERBOSE,
    };
  };

  public removeKeyCallback = (key: string): ActionResult => {
    if (key in this.ruleItemKeys) {
      delete this.ruleItemKeys[key];
      this.externalCallback();
      return {
        success: true,
        message: `Removed '${key}'.`,
        level: AlertLevel._4_LOG,
      };
    }
    alert(`Couldn't find key '${key}'.`);
    return {
      success: false,
      message: `Couldn't find key '${key}'.`,
      level: AlertLevel._0_CRITICAL,
    };
  };

  public getRuleItem = (key: string): RuleItem => {
    if (!(key in this.ruleItemKeys)) {
      throw Error(`Could not find rule item key '${key}'.`);
    }
    return this.getRuleModuleByRuleItem(key).getRuleItem(key);
  };

  public allRuleModules = (): RuleModule[] => {
    return this.ruleModules.flat();
  };

  public allRuleModuleSections = (): RuleModule[][] => {
    return this.ruleModules;
  };

  public getRuleModuleByKey = (key: string): RuleModule => {
    return this.allRuleModules()[this.ruleModuleIndex[key]];
  };

  public getRuleModuleByRuleItem = (key: string): RuleModule => {
    return this.allRuleModules()[this.ruleModuleIndex[this.ruleItemKeys[key]]];
  };

  public updateRuleModuleIndex = (): void => {
    this.ruleModuleIndex = {};
    let index: number = 0;
    this.ruleModules.forEach((section: RuleModule[]) => {
      section.forEach((ruleModule: RuleModule) => {
        if (ruleModule.key in this.ruleModuleIndex) {
          alert(`Duplicate rule module key '${ruleModule.key}'.`);
          return;
        }
        this.ruleModuleIndex[ruleModule.key] = index++;
      });
    });
  };

  public addRuleModule = (
    section_i: number,
    ruleModuleDefinition: IRuleModule
  ) => {
    // TODO: prevent xp modules and rule modules from having duplicate keys
    // TODO: logic around removing rule modules
    // TODO: set a flag to indicate the index isn't up to date
    if (ruleModuleDefinition.key in this.ruleModuleIndex) {
      const message = `Duplicate rule module name '${ruleModuleDefinition.key}'.`;
      alert(message);
      return {
        success: false,
        message,
      };
    }
    const ruleModule = new RuleModule(
      ruleModuleDefinition,
      () => this.externalCallback(),
      (key: string, triggerUI: boolean) =>
        this.addKeyCallback(key, triggerUI, ruleModuleDefinition.key),
      this.checkKeyDuplicateCallback,
      this.removeKeyCallback,
      this.addModuleAlert,
      this.addItemAlert
    );
    this.ruleModules[section_i].push(ruleModule);
    // TODO: have a method to rebuild this or combine it with the update index method
    this.moduleItemNameToModuleKey[ruleModule.ruleItemNameNormalized()] =
      ruleModuleDefinition.key;
  };

  public addRuleModuleAndRefresh = (
    section_i: number,
    ruleModuleDefinition: IRuleModule
  ): ActionResult => {
    this.addRuleModule(section_i, ruleModuleDefinition);
    this.refreshRules();
    this.externalCallback();
    return { success: true, message: "Success", level: AlertLevel._5_VERBOSE };
  };

  public addRuleItem = (
    name: string,
    key: string,
    moduleKey: string,
    ...bonusSources: string[]
  ): ActionResult => {
    const ruleItem: RuleItem = {
      name,
      key,
      details: {
        bonusCalculator: {
          sources: bonusSources.map((source) => {
            return { source };
          }),
        },
      },
    };
    const result = this.allRuleModules()[
      this.ruleModuleIndex[moduleKey]
    ].addItem(ruleItem);
    return result;
  };

  public deleteRuleItem = (key: string, moduleKey: string): ActionResult => {
    const result: ActionResult = this.allRuleModules()[
      this.ruleModuleIndex[moduleKey]
    ].deleteItem(key);
    return result;
  };
}
